<script setup lang="ts">
import type { ProductReviewFragment, ProductReviewsQuery } from '#graphql-operations'
import { SortOrder } from '#graphql-operations'
import { useInfiniteQuery } from '@pinia/colada'
import { onWatcherCleanup } from 'vue'

const props = defineProps<{
  reviewRating: number
  reviewCount: number
  productId: string
  productVariantId?: string | number
}>()

const route = useRoute()

const { state: reviews, loadMore, asyncStatus } = useInfiniteQuery({
  key: () => ['productReviewsFeed', route.params.id as string],
  enabled: import.meta.client,
  query: async ({ skip }) => skip != null
    ? useGraphqlQuery('productReviews', {
      id: props.productId,
      options: {
        skip,
        take: 5,
        sort: {
          createdAt: SortOrder.DESC,
        },
      },
    }) as Promise<{ data: ProductReviewsQuery }>
    : null,
  initialPage: {
    items: new Map<ProductReviewFragment['id'], ProductReviewFragment>(),
    totalItems: 0,
    skip: 0 as number | null,
  },
  merge(pages, data) {
    if (!data)
      return pages

    const newItems = new Map(pages.items)

    const newReviews = data.data.product?.reviews?.items || []
    newReviews.forEach(item => newItems.set(item.id, item))

    const totalItems = data.data.product?.reviews?.totalItems || 0
    const hasMorePages = newItems.size < totalItems

    return {
      items: newItems,
      totalItems,
      skip: hasMorePages ? newItems.size : null,
    }
  },
})

const loadMoreEl = useTemplateRef('load-more')

watch(loadMoreEl, (el) => {
  if (el) {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore()
        }
      },
      {
        rootMargin: '300px',
        threshold: [0],
      },
    )
    observer.observe(el)
    onWatcherCleanup(() => {
      observer.disconnect()
    })
  }
})

const { t } = useI18n()
</script>

<template>
  <UiCard class="wfull space-y-8">
    <UiCardHeader :class="{ 'border-b': reviews.data?.items?.size }">
      <div v-if="reviewRating && reviewCount" class="mb4 space-y-4">
        <div class="flex text-lg fw600 divide-x divide-border">
          <span class="mr2">{{ reviewCount }} {{ t('product.reviews.label') }}</span>

          <div class="flex items-center">
            <span class="mr2 pl2">{{ reviewRating }}</span>
            <Icon
              v-for="n in 5" :key="n"
              :name="n <= Math.floor(reviewRating) ? 'ph:star-fill' : 'ph:star-duotone'"
              class="c-amber4"
            />
          </div>
        </div>

        <ContentTip icon="i-ri:shield-user-fill">
          {{ t('product.review_verified_purchases') }}
        </ContentTip>
      </div>

      <div v-else class="flex flex-col items-center text-sm">
        <span class="fw600">
          {{ t('general.no_reviews.label') }}
        </span>
        <span class="text-muted-foreground">
          {{ t('product.no_reviews.description') }}
        </span>
      </div>

      <ProductReviewCreateModal :product-id :product-variant-id />
    </UiCardHeader>

    <UiCardContent v-if="reviewCount">
      <!-- show only 3 reviews -->
      <template v-if="(asyncStatus === 'idle' || asyncStatus === 'loading') && reviewCount > 0 && !reviews.data?.items?.size">
        <ProductReviewListItemSkeleton v-for="i in 3" :key="`skeleton-${i}`" />
      </template>
      <LazyProductReviewList
        v-else
        :reviews="Array.from(reviews.data.items.values()).slice(0, 3)"
      />

      <UiDialog>
        <div class="text-center">
          <UiDialogTrigger as-child>
            <UiButton
              v-if="(asyncStatus === 'idle' || asyncStatus === 'loading') && reviewCount > 3 && reviews.data?.items?.size"
              :disabled="asyncStatus === 'loading'"
              variant="outline"
              class="rounded-full"
            >
              {{ t('product_reviews.show_all') }}
            </UiButton>
          </UiDialogTrigger>
        </div>
        <UiDialogContent class="max-h-[90dvh] max-w-xl flex flex-col p-0">
          <div class="relative">
            <UiDialogHeader class="p-6 pb-0">
              <UiDialogTitle>
                {{ t('product_reviews.all_reviews') }}
              </UiDialogTitle>
            </UiDialogHeader>

            <div class="pointer-events-none absolute inset-x-0 z-10 h-12 from-background to-transparent from-50% bg-gradient-to-b" />
          </div>

          <div class="grid gap-4 overflow-y-auto px-6 py-4">
            <div class="flex flex-col justify-between">
              <template v-if="asyncStatus === 'loading' && !reviews.data.items.size">
                <ProductReviewListItemSkeleton
                  v-for="i in 3"
                  :key="`skeleton-${i}`"
                />
              </template>
              <LazyProductReviewList
                v-else
                :reviews="Array.from(reviews.data.items.values())"
              />
              <div v-if="reviews.data.skip" ref="load-more" class="flex justify-center">
                <svg class="h-4 w-4 animate-spin text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            </div>
          </div>
        </UiDialogContent>
      </UiDialog>
    </UiCardContent>
  </UiCard>
</template>
